// ════════════════════════════════════════════════════════════════════
// Almacenamiento SQLite para el APK de BAC Sarampión (Capacitor)
// ════════════════════════════════════════════════════════════════════
// Imita la parte de la API de Dexie que usa BAC (get, add, put, bulkPut,
// update, delete, bulkDelete, clear, toArray, count, filter, where().
// equals/notEqual/anyOf, and, first), así el resto del código no cambia:
// en el navegador sigue Dexie (IndexedDB) y dentro del APK los datos van
// a un archivo SQLite propio de la app, que NO se borra al limpiar Chrome.
//
// Formato: una sola tabla "kv" (store, pk, part, data). Cada registro se
// guarda como JSON; si es muy grande (cachés de una comunidad completa)
// se parte en pedazos ("part") porque Android no puede leer filas de
// más de ~2 MB de una sola vez.
// ════════════════════════════════════════════════════════════════════

const NOMBRE_BD = "bac_sarampion";
const TAM_PEDAZO = 400000; // caracteres por pedazo (muy por debajo de 2 MB)

// Mismas tablas y claves que localDB.js (Dexie v8).
//   auto: true  → "++id" (id numérico autoincremental)
//   key         → clave primaria tomada del propio objeto
export const ESQUEMA_BAC = {
  bac_visitas_pendientes:       { auto: true },
  bac_visitas_cache:            { key: ["territorio", "comunidad"] },
  bac_fotos_pendientes:         { auto: true },
  bac_catalogos_cache:          { key: "tipo" },
  bac_pai_cache:                { key: ["territorio", "comunidad"] },
  bac_mrcv_cache:               { key: ["territorio", "comunidad"] },
  bac_disentimiento_pendientes: { auto: true },
  bac_disentimiento_cache:      { key: "clave" },
  bac_jornadas_cache:           { key: ["territorio", "comunidad"] },
  bac_jornadas_pendientes:      { auto: true },
};

export const esApk = () => Boolean(window.Capacitor?.isNativePlatform?.());

let conexion = null;

// Todas las operaciones pasan en fila, una tras otra: así un update
// (leer → mezclar → escribir) nunca se cruza con otro sobre el mismo
// registro.
let cola = Promise.resolve();
function enCola(fn) {
  const p = cola.then(() => fn());
  cola = p.catch(() => {});
  return p;
}

// ── Apertura ───────────────────────────────────────────────────────────
async function abrir(dexieDb) {
  const { CapacitorSQLite, SQLiteConnection } = await import("@capacitor-community/sqlite");
  const sqlite = new SQLiteConnection(CapacitorSQLite);
  try { await sqlite.checkConnectionsConsistency(); } catch { /* sin conexiones previas */ }
  const yaExiste = (await sqlite.isConnection(NOMBRE_BD, false)).result;
  const c = yaExiste
    ? await sqlite.retrieveConnection(NOMBRE_BD, false)
    : await sqlite.createConnection(NOMBRE_BD, false, "no-encryption", 1, false);
  await c.open();
  await c.execute(`
    CREATE TABLE IF NOT EXISTS kv (
      store TEXT NOT NULL,
      pk    TEXT NOT NULL,
      ord   REAL,
      part  INTEGER NOT NULL,
      data  TEXT NOT NULL,
      PRIMARY KEY (store, pk, part)
    );
    CREATE INDEX IF NOT EXISTS kv_orden ON kv (store, ord, pk, part);
    CREATE TABLE IF NOT EXISTS seq (store TEXT PRIMARY KEY, last INTEGER NOT NULL);
  `);
  conexion = c;
  console.log("🗄️ BAC: almacenamiento SQLite listo");

  try { await migrarDesdeDexie(dexieDb); }
  catch (e) { console.warn("⚠️ BAC: no se pudo migrar lo que había en IndexedDB:", e?.message || e); }
}

// Si una versión anterior del APK guardó pendientes en IndexedDB (Dexie),
// se pasan una sola vez a SQLite y luego se vacía IndexedDB.
async function migrarDesdeDexie(dexieDb) {
  if (!dexieDb) return;
  for (const store of Object.keys(ESQUEMA_BAC)) {
    let filas;
    try { filas = await dexieDb.table(store).toArray(); } catch { continue; }
    if (!filas?.length) continue;

    const set = [];
    let maxId = 0;
    for (const fila of filas) {
      set.push(...sentenciasEscribir(store, claveDe(store, fila), fila));
      if (ESQUEMA_BAC[store].auto) maxId = Math.max(maxId, Number(fila.id) || 0);
    }
    if (maxId) {
      const actual = await ultimoId(store);
      set.push({ statement: "INSERT OR REPLACE INTO seq (store, last) VALUES (?, ?)", values: [store, Math.max(actual, maxId)] });
    }
    await conexion.executeSet(set, true);
    await dexieDb.table(store).clear();
    console.log(`🔁 BAC: migrados ${filas.length} registros de ${store} a SQLite`);
  }
}

// ── Utilidades internas (asumen que ya se está dentro de la cola) ─────
function exigirConexion() {
  if (!conexion) throw new Error("Almacenamiento SQLite de BAC no disponible");
}

const textoClave = (k) => JSON.stringify(k);

function claveDe(store, obj) {
  const cfg = ESQUEMA_BAC[store];
  if (cfg.auto) return obj.id;
  return Array.isArray(cfg.key) ? cfg.key.map((f) => obj[f]) : obj[cfg.key];
}

// Parte el texto sin cortar por la mitad un emoji/carácter especial
// (par sustituto UTF-16), que se dañaría al guardarse.
function pedazos(texto) {
  const out = [];
  let i = 0;
  while (i < texto.length) {
    let fin = Math.min(i + TAM_PEDAZO, texto.length);
    const c = texto.charCodeAt(fin - 1);
    if (fin < texto.length && c >= 0xd800 && c <= 0xdbff) fin -= 1;
    out.push(texto.slice(i, fin));
    i = fin;
  }
  return out.length ? out : [""];
}

function sentenciasEscribir(store, clave, obj) {
  const pk = textoClave(clave);
  const ord = ESQUEMA_BAC[store].auto ? Number(clave) : null;
  const set = [{ statement: "DELETE FROM kv WHERE store = ? AND pk = ?", values: [store, pk] }];
  pedazos(JSON.stringify(obj)).forEach((data, part) => {
    set.push({
      statement: "INSERT INTO kv (store, pk, ord, part, data) VALUES (?, ?, ?, ?, ?)",
      values: [store, pk, ord, part, data],
    });
  });
  return set;
}

function unirFilas(valores) {
  const porClave = new Map(); // conserva el orden de llegada (ord, pk, part)
  for (const f of valores || []) porClave.set(f.pk, (porClave.get(f.pk) || "") + f.data);
  return [...porClave.values()].map((t) => JSON.parse(t));
}

async function leerTodos(store) {
  exigirConexion();
  const r = await conexion.query(
    "SELECT pk, data FROM kv WHERE store = ? ORDER BY ord, pk, part", [store]);
  return unirFilas(r.values);
}

async function leerUno(store, clave) {
  exigirConexion();
  const r = await conexion.query(
    "SELECT pk, data FROM kv WHERE store = ? AND pk = ? ORDER BY part", [store, textoClave(clave)]);
  return unirFilas(r.values)[0];
}

async function ultimoId(store) {
  const r = await conexion.query("SELECT last FROM seq WHERE store = ?", [store]);
  return Number(r.values?.[0]?.last) || 0;
}

async function escribir(store, obj, { soloNuevo = false } = {}) {
  exigirConexion();
  const cfg = ESQUEMA_BAC[store];
  const set = [];
  let clave = claveDe(store, obj);

  if (cfg.auto && (clave === undefined || clave === null)) {
    clave = (await ultimoId(store)) + 1;
    obj.id = clave; // igual que Dexie: el objeto queda con su id
    set.push({ statement: "INSERT OR REPLACE INTO seq (store, last) VALUES (?, ?)", values: [store, clave] });
  } else if (soloNuevo && (await leerUno(store, clave)) !== undefined) {
    const e = new Error(`Ya existe un registro con esa clave en ${store}`);
    e.name = "ConstraintError";
    throw e;
  } else if (cfg.auto && Number(clave) > (await ultimoId(store))) {
    set.push({ statement: "INSERT OR REPLACE INTO seq (store, last) VALUES (?, ?)", values: [store, Number(clave)] });
  }

  set.push(...sentenciasEscribir(store, clave, obj));
  await conexion.executeSet(set, true);
  return clave;
}

async function borrar(store, claves) {
  exigirConexion();
  if (!claves.length) return;
  await conexion.executeSet(
    claves.map((k) => ({ statement: "DELETE FROM kv WHERE store = ? AND pk = ?", values: [store, textoClave(k)] })),
    true);
}

function aplicarCambios(obj, cambios) {
  if (typeof cambios === "function") { cambios(obj); return obj; }
  return { ...obj, ...cambios };
}

// ── Colección (resultado de where / filter) ───────────────────────────
class Coleccion {
  constructor(tabla, filtros) { this.tabla = tabla; this.filtros = filtros; }

  and(fn)    { return new Coleccion(this.tabla, [...this.filtros, fn]); }
  filter(fn) { return this.and(fn); }

  _coinciden() {
    return leerTodos(this.tabla.store).then((filas) =>
      filas.filter((f) => this.filtros.every((fn) => fn(f))));
  }

  toArray() { return enCola(() => this._coinciden()); }
  first()   { return enCola(async () => (await this._coinciden())[0]); }
  count()   { return enCola(async () => (await this._coinciden()).length); }

  delete() {
    return enCola(async () => {
      const filas = await this._coinciden();
      await borrar(this.tabla.store, filas.map((f) => claveDe(this.tabla.store, f)));
      return filas.length;
    });
  }

  modify(cambios) {
    return enCola(async () => {
      const filas = await this._coinciden();
      for (const f of filas) await escribir(this.tabla.store, aplicarCambios(f, cambios));
      return filas.length;
    });
  }
}

// ── Tabla ──────────────────────────────────────────────────────────────
class Tabla {
  constructor(store) { this.store = store; }

  get(clave)     { return enCola(() => leerUno(this.store, clave)); }
  toArray()      { return enCola(() => leerTodos(this.store)); }
  count()        { return enCola(async () => (await leerTodos(this.store)).length); }
  add(obj)       { return enCola(() => escribir(this.store, obj, { soloNuevo: true })); }
  put(obj)       { return enCola(() => escribir(this.store, obj)); }
  delete(clave)  { return enCola(() => borrar(this.store, [clave])); }
  bulkDelete(cl) { return enCola(() => borrar(this.store, cl || [])); }

  bulkPut(objs) {
    return enCola(async () => {
      let ultima;
      for (const o of objs || []) ultima = await escribir(this.store, o);
      return ultima;
    });
  }

  update(clave, cambios) {
    return enCola(async () => {
      const actual = await leerUno(this.store, clave);
      if (actual === undefined) return 0;
      await escribir(this.store, aplicarCambios(actual, cambios));
      return 1;
    });
  }

  clear() {
    return enCola(async () => {
      exigirConexion();
      await conexion.executeSet([
        { statement: "DELETE FROM kv WHERE store = ?", values: [this.store] },
      ], true);
    });
  }

  filter(fn) { return new Coleccion(this, [fn]); }

  where(campo) {
    const col = (fn) => new Coleccion(this, [fn]);
    // Igual que un índice de Dexie: registros sin ese campo no entran.
    const tiene = (r) => r[campo] !== undefined && r[campo] !== null;
    return {
      equals:   (v)   => col((r) => tiene(r) && r[campo] === v),
      notEqual: (v)   => col((r) => tiene(r) && r[campo] !== v),
      anyOf:    (arr) => col((r) => tiene(r) && (arr || []).includes(r[campo])),
    };
  }
}

// ── Punto de entrada ───────────────────────────────────────────────────
// Devuelve un objeto con las mismas propiedades que el db de Dexie
// (db.bac_visitas_pendientes, db.bac_fotos_pendientes, ...).
export function crearDbSqlite(dexieDb) {
  enCola(() => abrir(dexieDb)).catch((e) =>
    console.error("❌ BAC: no se pudo abrir SQLite:", e?.message || e));
  const db = {};
  for (const store of Object.keys(ESQUEMA_BAC)) db[store] = new Tabla(store);
  db.table = (nombre) => db[nombre];
  return db;
}
